

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiRequest } from "../../api/apiHelper";
import CustomFilterBar, { DATE_OPTIONS } from "../../components/userRing/CustomFilterBar";

const STAGES = [
  { key: "pending", label: "Pending" },
  { key: "overdue", label: "Overdue" },
  { key: "completed", label: "Completed" },
];



export default function TaskList({ onClose, fromManagerDashboard = false, members: passedMembers = [] }) {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location?.state?.from;
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dateRange, setDateRange] = useState("");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [visibleCounts, setVisibleCounts] = useState(() =>
    STAGES.reduce((acc, s) => {
      acc[s.key] = 10;
      return acc;
    }, {})
  );

  // Normalize ManagerDashboard.teamTaskSummary => flat tasks (includes overdue)
  function normalizeTeamSummaryArray(arr) {
    if (!Array.isArray(arr)) return [];
    return arr.flatMap((u, idx) => {
      const name = u.user_name || `user_${idx}`;
      const mapTask = (t, i, s, dateField) => ({
        id: `${name.replace(/\s+/g, "_").toLowerCase()}-${s}-${i}`,
        title: t.taskTitle || t.title || t.taskName || `${name} — task`,
        status: normalizeStatus(s || ""),
        createdAt: t[dateField] || t.createdAt || new Date().toISOString().slice(0, 10),
        assignedTo: name,
      });

      const pending = (u.pending_task_today || []).map((t, i) => mapTask(t, i, "pending", "createdAt"));
      const inprog = (u.in_progress_tasks || []).map((t, i) => mapTask(t, i, "in-progress", "startedAt"));
      const overdue = (u.overdue_tasks || []).map((t, i) => mapTask(t, i, "overdue", "dueDate"));
      const completed = (u.completed_tasks || []).map((t, i) => mapTask(t, i, "completed", "completedAt"));

      return [...pending, ...inprog, ...overdue, ...completed];
    });
  }

  // Normalize status string to match pipeline keys (e.g. "In Progress" -> "in-progress")
  function normalizeStatus(s) {
    if (!s) return "";
    return String(s)
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, "-")
      .replace(/[^a-z0-9-]/g, "");
  }

  useEffect(() => {
    let mounted = true;

    async function loadTasks() {
      try {
        // Always fetch from backend with date filter
        let params = {};
        // Robust userId detection: from location.state, or from URL if not present
        let userId = location?.state?.userId;
        if (!userId) {
          // Try to extract from URL: /member/:userId
          const match = window.location.pathname.match(/\/member\/([a-zA-Z0-9]+)/);
          if (match && match[1]) userId = match[1];
        }
        params.userId = userId || "";

        if (dateRange) {
          let s = null, e = null;
          
          // Check if dateRange is an object with startDate/endDate (from CustomFilterBar Apply button)
          if (typeof dateRange === 'object' && dateRange.startDate && dateRange.endDate) {
            params.startDate = dateRange.startDate;
            params.endDate = dateRange.endDate;
          } else if (dateRange !== "custom") {
            // Numeric filter (e.g., "7", "14", "28", "90")
            const days = Number(dateRange);
            if (!Number.isNaN(days) && days > 0) {
              const now = new Date();
              s = new Date(now);
              s.setDate(now.getDate() - (days - 1)); // inclusive range
              s.setHours(0, 0, 0, 0);
              e = new Date(now);
              e.setHours(23, 59, 59, 999);
            }
            if (s && e) {
              params.startDate = s.toISOString().slice(0, 10);
              params.endDate = e.toISOString().slice(0, 10);
            }
          } else {
            // custom date selected but not yet applied: use customStart/customEnd state
            if (!customStart || !customEnd) return; // don't call API if either is missing
            if (customEnd < customStart) return; // don't call API if end < start
            params.startDate = customStart;
            params.endDate = customEnd;
          }
        }

        let query = "/tasks/get";
        const queryParams = Object.entries(params)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join("&");
        if (queryParams) query += `?${queryParams}`;

        try {
          const res = await apiRequest("get", query);
          const arr = Array.isArray(res.data) ? res.data : [];
          const normalized = arr.map((t, i) => ({
            id: t._id || t.id || `task-${i}`,
            title: t.taskTitle || t.title || "",
            status: normalizeStatus(t.status),
            createdAt: t.createdAt ? new Date(t.createdAt).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
            assignedTo: (t.assignedUser && t.assignedUser.name) || t.assignedTo || "Unassigned",
            raw: t,
          }));

          if (mounted) {
            setTasks(normalized);
            try {
              // eslint-disable-next-line no-console
              console.debug("TaskList: normalized tasks:", normalized);
            } catch (e) {}
            return;
          }
        } catch (e) {
          console.warn("Failed to fetch tasks from API:", e.message || e);
        }

        // No data available: set empty list
        if (mounted) {
          setTasks([]);
        }
      } catch (err) {
        console.warn("loadTasks error:", err);
      }
    }

    loadTasks();

    return () => {
      mounted = false;
    };
  }, [dateRange, customStart, customEnd]);

  // Handle click on a task item. If opened from Manager Dashboard and the task
  // is assigned, try to resolve the assigned user and navigate to their member page.
  const handleTaskClick = (t) => {
    // assignedTo may be a name or id depending on source
    const assignee = t.assignedTo || (t.raw && (t.raw.assignedTo || t.raw.assigned_to)) || null;
    if (fromManagerDashboard && assignee) {
      const allMembers = Array.isArray(passedMembers) && passedMembers.length ? passedMembers : teamMembers;
      // Try matching by id first
      let found = allMembers.find((m) => String(m.id) === String(assignee) || String(m._id) === String(assignee) || String(m.userId) === String(assignee));
      if (!found) {
        // Try matching by name (case-insensitive substring)
        const assigneeName = String(assignee).toLowerCase();
        found = allMembers.find((m) => (m.name && m.name.toLowerCase() === assigneeName) || (m.name && m.name.toLowerCase().includes(assigneeName)) || (m.userName && String(m.userName).toLowerCase().includes(assigneeName)));
      }

      if (found) {
        try {
          if (onClose) onClose();
        } catch (e) {}
        // include selected task id/raw so UserDetails can highlight and scroll to the task
        const selectedId = t.id || t._id || (t.raw && (t.raw._id || t.raw.id));
        navigate(`/home/member/${found.id || found._id}`, {
          state: { from: 'manager-dashboard', member: found, selectedTaskId: selectedId, selectedTask: t.raw || t },
        });
        return;
      }
    }

    // Fallback: show task detail modal as before
    setSelectedTask(t);
  };

  useEffect(() => {
    async function fetchMembers() {
      try {
        const res = await apiRequest("get", "/invite/get-team-member");
        if (res.data && res.data.invites) {
          const members = res.data.invites.filter((m) => m.status === "complete" && m.role === "member");
          setTeamMembers(members);
        }
      } catch (err) {
        // ignore
      }
    }
    fetchMembers();
  }, []);

  // date filter
  // compute inclusive start/end for date filtering (use full-day ranges)


  // No frontend date filtering, backend already filtered
  const filtered = tasks;

  // Debug: log computed range and per-task inclusion for troubleshooting
  useEffect(() => {
    try {
      // Debug: date filtering is now backend-only
    } catch (e) {}
  }, [dateRange, customStart, customEnd, tasks]);

  // Group tasks by status
  const grouped = {
    pending: filtered.filter((t) => normalizeStatus(t.status) === "pending"),
    "in-progress": filtered.filter((t) => normalizeStatus(t.status) === "in-progress"),
    overdue: filtered.filter((t) => normalizeStatus(t.status) === "overdue"),
    completed: filtered.filter((t) => normalizeStatus(t.status) === "completed"),
  };

  function handleShowMore(stageKey) {
    setVisibleCounts((prev) => {
      const current = typeof prev[stageKey] === "number" ? prev[stageKey] : 10;
      return { ...prev, [stageKey]: current + 5 };
    });
  }
  function handleShowLess(stageKey) {
    setVisibleCounts((prev) => ({ ...prev, [stageKey]: 10 }));
  }

  // Helper: resolve assignee name from various shapes (id, name, raw.assignedUser)
  function findMemberName(val) {
    if (!val) return null;
    const all = Array.isArray(passedMembers) && passedMembers.length ? passedMembers : teamMembers;
    if (!all || !all.length) return null;
    let found = all.find((m) => String(m.id) === String(val) || String(m._id) === String(val) || String(m.userId) === String(val));
    if (found) return found.name || found.userName || found.email || null;
    const lower = String(val).toLowerCase();
    found = all.find((m) => (m.name && m.name.toLowerCase() === lower) || (m.name && m.name.toLowerCase().includes(lower)));
    if (found) return found.name;
    return null;
  }

  function resolveAssigneeName(t) {
    if (!t) return "Unassigned";
    const raw = t.raw || {};
    // prefer explicit assignedUser from raw (API canonical)
    if (raw.assignedUser && (raw.assignedUser.name || raw.assignedUser.email)) return raw.assignedUser.name || raw.assignedUser.email;
    if (t.assignedUser && (t.assignedUser.name || t.assignedUser.email)) return t.assignedUser.name || t.assignedUser.email;
    // then try mapping by id
    if (t.assignedTo && typeof t.assignedTo === 'string') {
      const name = findMemberName(t.assignedTo);
      if (name) return name;
      if (t.assignedTo.length && t.assignedTo.length < 40 && /[a-zA-Z]/.test(t.assignedTo)) return t.assignedTo;
    }
    if (raw.assignedTo && typeof raw.assignedTo === 'string') {
      const name = findMemberName(raw.assignedTo);
      if (name) return name;
    }
    return "Unassigned";
  }

  function formatDateShort(d) {
    if (!d) return "";
    try {
      const dt = new Date(d);
      if (isNaN(dt)) return String(d).slice(0, 10);
      return dt.toISOString().slice(0, 10);
    } catch (e) {
      return String(d).slice(0, 10);
    }
  }

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid #e5e7eb" }}>
        {/* <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: "#1e293b" }}>Task Pipeline</h2> */}
        
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <CustomFilterBar
            dateRange={dateRange}
            setDateRange={setDateRange}
            customStart={customStart}
            setCustomStart={setCustomStart}
            customEnd={customEnd}
            setCustomEnd={setCustomEnd}
            DATE_OPTIONS={DATE_OPTIONS}
          />
        </div>
      </div>

      {/* Debug panel: shows date filter active and count */}
      {dateRange && (
        <div style={{ marginBottom: 8, fontSize: 13, color: "#333" }}>
          <strong>Date filter active</strong>
          &nbsp;•&nbsp;<strong>Tasks shown:</strong> {filtered.length}/{tasks.length}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${STAGES.length}, minmax(180px, 1fr))`, gap: 12 }}>
        {STAGES.map((s) => {
          const list = grouped[s.key] || [];
          const visible = typeof visibleCounts[s.key] === "number" ? visibleCounts[s.key] : 10;
          const showList = list.slice(0, visible);
          const shouldShowMore = list.length > visible;
          const shouldShowLess = list.length > 10 && visible > 10;

          return (
              <div
                key={s.key}
                style={{
                  background: "#f7f8fb",
                  borderRadius: 10,
                  padding: 10,
                  minHeight: "55vh",
                  minWidth: 160,
                  boxSizing: 'border-box',
                  border: "1px solid #e6e9f2",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong style={{ textTransform: "capitalize" }}>{s.label}</strong>
                <span style={{ color: "#666" }}>{list.length}</span>
              </div>

              {list.length === 0 ? (
                <div style={{ color: "#aaa", textAlign: "center", marginTop: 30 }}>No tasks</div>
              ) : (
                <>
                  <div style={{ overflowY: "auto", flex: 1, paddingRight: 6 }}>
                    {showList.map((t) => {
                      const assignee = resolveAssigneeName(t);
                      const dateStr = formatDateShort(t.createdAt || (t.raw && (t.raw.createdAt || t.raw.startedAt || t.raw.dueDate)));
                      return (
                        <div
                          key={t.id}
                          onClick={() => handleTaskClick(t)}
                          style={{
                            background: "#fff",
                            padding: 10,
                            borderRadius: 8,
                            marginBottom: 8,
                            cursor: "pointer",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                            borderLeft: `4px solid ${
                              s.key === "pending" ? "#f0ad4e" : s.key === "in-progress" ? "#1976d2" : s.key === "overdue" ? "#d32f2f" : "#4caf50"
                            }`,
                            width: '100%',
                            boxSizing: 'border-box',
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: '1.2em',
                            }}
                          >
                            {t.title}
                          </div>

                          <div style={{ display: "flex", justifyContent: "space-between", color: "#666", marginTop: 8, fontSize: 13 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#666' }}>
                              <span style={{ fontSize: 14 }}>👤</span>
                              <span style={{ color: '#666', fontSize: 13 }}>{assignee}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', color: '#666' }}>
                              <span style={{ fontSize: 14 }}>📅</span>
                              <span style={{ color: '#666', fontSize: 13 }}>{dateStr}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {shouldShowMore && (
                    <div style={{ textAlign: "center", marginTop: 8 }}>
                      <button
                        onClick={() => handleShowMore(s.key)}
                        style={{ border: "none", background: "transparent", color: "#1976d2", cursor: "pointer" }}
                      >
                        Show more (+5)
                      </button>
                    </div>
                  )}

                  {shouldShowLess && (
                    <div style={{ textAlign: "center", marginTop: 6 }}>
                      <button
                        onClick={() => handleShowLess(s.key)}
                        style={{ border: "none", background: "transparent", color: "#666", cursor: "pointer", fontSize: 13 }}
                      >
                        Show less
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {selectedTask && (
        <div
          onClick={() => setSelectedTask(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", padding: 20, borderRadius: 12, minWidth: 320 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>{selectedTask.title}</h3>
              <button onClick={() => setSelectedTask(null)} style={{ border: "none", background: "transparent", fontSize: 20, cursor: "pointer" }}>
                ×
              </button>
            </div>
            <p style={{ margin: "8px 0" }}><b>Status:</b> {selectedTask.status}</p>
            <p style={{ margin: "8px 0" }}><b>Assigned to:</b> {selectedTask.assignedTo}</p>
            <p style={{ margin: "8px 0" }}><b>Date:</b> {selectedTask.createdAt}</p>
          </div>
        </div>
      )}
    </div>
  );
}

